import { useState, useEffect, useRef, useCallback } from 'react'
import { getDataAPI, postDataAPI, getActiveArtistIds, getPrimaryArtistId } from '../lib/api'
import type {
  SectionGranularConfig,
  ShowsGranularConfig,
  MusicGranularConfig,
  ScheduleGranularConfig,
  OverviewGranularConfig,
  FinanceGranularConfig,
  TasksGranularConfig,
  KnowledgeGranularConfig,
  AccessLevel,
} from '../types/permissions'
import { getDefaultSectionConfig } from '../types/permissions'

// ─── Types ───────────────────────────────────────────────────────────

type UserPermissions = {
  sections: string[]
  sectionConfig: SectionGranularConfig
}

// ─── Legacy app → section mapping (still used for backends that only
//     return an `apps` array without granular config) ─────────────────

const APP_TO_SECTIONS: Record<string, string[]> = {
  bookings:   ['shows', 'schedule'],
  tourmngr:   ['shows', 'schedule'],
  catalog:    ['music'],
  knowledge:  ['knowledge'],
  workflows:  ['schedule'],
  financials: ['finance'],
  branddeals: ['deals'],
}

/**
 * Recursively search an object for an `apps` array (string[]).
 * Returns the first match found, or null.
 */
function findAppsArray(obj: any, depth = 0): string[] | null {
  if (!obj || typeof obj !== 'object' || depth > 5) return null
  if (Array.isArray(obj.apps) && obj.apps.every((a: any) => typeof a === 'string')) {
    return obj.apps
  }
  for (const key of ['data', 'permissions', 'result', 'member', 'teamMember']) {
    if (obj[key] && typeof obj[key] === 'object') {
      const found = findAppsArray(obj[key], depth + 1)
      if (found) return found
    }
  }
  return null
}

/** Recursively search for a `sectionConfig` object from the API response */
function findSectionConfig(obj: any, depth = 0): SectionGranularConfig | null {
  if (!obj || typeof obj !== 'object' || depth > 5) return null
  if (obj.sectionConfig && typeof obj.sectionConfig === 'object') {
    return obj.sectionConfig as SectionGranularConfig
  }
  for (const key of ['data', 'permissions', 'result', 'member', 'teamMember']) {
    if (obj[key] && typeof obj[key] === 'object') {
      const found = findSectionConfig(obj[key], depth + 1)
      if (found) return found
    }
  }
  return null
}

/** Recursively search for a `sections` array from the API response */
function findSectionsArray(obj: any, depth = 0): string[] | null {
  if (!obj || typeof obj !== 'object' || depth > 5) return null
  if (Array.isArray(obj.sections) && obj.sections.every((s: any) => typeof s === 'string')) {
    return obj.sections
  }
  for (const key of ['data', 'permissions', 'result', 'member', 'teamMember']) {
    if (obj[key] && typeof obj[key] === 'object') {
      const found = findSectionsArray(obj[key], depth + 1)
      if (found) return found
    }
  }
  return null
}

/** Given the raw `apps` array, derive the set of allowed route sections. */
function deriveSections(apps: string[]): string[] {
  const sections = new Set<string>()
  sections.add('team')
  sections.add('settings')
  sections.add('schedule')
  for (const appId of apps) {
    const mapped = APP_TO_SECTIONS[appId]
    if (mapped) mapped.forEach((s) => sections.add(s))
  }
  return Array.from(sections)
}

type ResolvedArtistPerms = {
  sections: string[] | null
  apps: string[] | null
  sectionConfig: SectionGranularConfig | null
  memberId: string | null
}

/**
 * Load team/member-permissions for one artist (POST + optional GET + localStorage fallback).
 * Used for blended multi-artist mode: union section IDs across all active artists.
 */
async function resolvePermissionsForArtist(
  artistId: string,
  userData: { email: string; _id?: string },
): Promise<ResolvedArtistPerms> {
  let apps: string[] | null = null
  let sections: string[] | null = null
  let sectionConfig: SectionGranularConfig | null = null
  let res: any = null
  let memberId: string | null = null

  try {
    res = await postDataAPI('team/member-permissions', {
      artistId,
      teamMemberEmail: userData.email,
    })

    const mid =
      (res as { data?: { memberId?: string; data?: { memberId?: string } } })?.data?.memberId ??
      (res as { data?: { data?: { memberId?: string } } })?.data?.data?.memberId ??
      (res as { memberId?: string })?.memberId
    if (typeof mid === 'string' && mid) memberId = mid

    sections = findSectionsArray(res)
    sectionConfig = findSectionConfig(res)
    if (!sections) {
      apps = findAppsArray(res)
    }
  } catch (err) {
    console.warn('[useUserPermissions] POST team/member-permissions failed:', err)
  }

  if (!sections && !apps) {
    try {
      const mid = memberId ?? res?.data?.memberId ?? res?.memberId
      if (mid) {
        const res2 = await getDataAPI(`team/member/${mid}`)
        sections = findSectionsArray(res2)
        if (!sectionConfig) sectionConfig = findSectionConfig(res2)
        if (!sections) apps = findAppsArray(res2)
      }
    } catch (err2) {
      console.warn('[useUserPermissions] GET team/member fallback failed:', err2)
    }
  }

  if (!sectionConfig) {
    try {
      const keys = [userData._id, memberId].filter((k): k is string => typeof k === 'string' && k.length > 0)
      for (const k of keys) {
        const localRaw = localStorage.getItem(`dizee:granularPerms:${artistId}:${k}`)
        if (localRaw) {
          sectionConfig = JSON.parse(localRaw) as SectionGranularConfig
          break
        }
      }
    } catch {
      /* ignore */
    }
  }

  return { sections, apps, sectionConfig, memberId }
}

const TEAM_PERMS_SNAPSHOT_PREFIX = 'dizee:teamPermsSnapshot:'

function teamPermsSnapshotKey(teamMemberEmail: string, primaryArtistId: string): string {
  const safe = teamMemberEmail.replace(/[|:]/g, '_')
  return `${TEAM_PERMS_SNAPSHOT_PREFIX}${safe}:${primaryArtistId}`
}

function readTeamPermissionsSnapshot(teamMemberEmail: string, primaryArtistId: string): UserPermissions | null {
  try {
    const raw = localStorage.getItem(teamPermsSnapshotKey(teamMemberEmail, primaryArtistId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as { sections?: unknown; sectionConfig?: unknown }
    if (!Array.isArray(parsed.sections) || !parsed.sections.every((s) => typeof s === 'string')) return null
    return {
      sections: parsed.sections,
      sectionConfig: (parsed.sectionConfig && typeof parsed.sectionConfig === 'object'
        ? parsed.sectionConfig
        : {}) as SectionGranularConfig,
    }
  } catch {
    return null
  }
}

function writeTeamPermissionsSnapshot(teamMemberEmail: string, primaryArtistId: string, perms: UserPermissions): void {
  try {
    localStorage.setItem(
      teamPermsSnapshotKey(teamMemberEmail, primaryArtistId),
      JSON.stringify({ sections: perms.sections, sectionConfig: perms.sectionConfig }),
    )
  } catch {
    /* quota */
  }
}

// ─── Hook ────────────────────────────────────────────────────────────

export function useUserPermissions() {
  // ── Synchronously resolve the fast paths (artist / no-user / no-artist)
  //    so ProtectedRoute never flashes "Loading..." for these cases. ──
  const { initPerms, initLoading, initIsTeamMember, initPrimaryArtistId, initUserData } = (() => {
    try {
      const user = localStorage.getItem('dizeeUser')
      const token = localStorage.getItem('dizeeToken')
      if (!user || !token) return { initPerms: null, initLoading: false, initIsTeamMember: false, initPrimaryArtistId: null, initUserData: null }
      const userData = JSON.parse(user)
      const isTM = ['manager', 'support', 'promoter'].includes(userData.professionRole)
      // Artists get full access immediately — no async work needed
      if (!isTM) return { initPerms: null, initLoading: false, initIsTeamMember: false, initPrimaryArtistId: null, initUserData: userData }
      const activeIds = getActiveArtistIds()
      // Team member with no active artists — base sections only (no activity until permissions are fetched)
      if (activeIds.length === 0) return { initPerms: { sections: ['team', 'settings', 'schedule'], sectionConfig: {} } as UserPermissions, initLoading: false, initIsTeamMember: true, initPrimaryArtistId: null, initUserData: userData }
      // Team + active artists: hydrate from last session snapshot so the sidebar matches what they had (no generic default strip).
      const email = typeof userData.email === 'string' ? userData.email : ''
      const snap = email ? readTeamPermissionsSnapshot(email, activeIds[0]) : null
      return {
        initPerms: snap,
        initLoading: true,
        initIsTeamMember: true,
        initPrimaryArtistId: activeIds[0],
        initUserData: userData,
      }
    } catch {
      return { initPerms: null, initLoading: false, initIsTeamMember: false, initPrimaryArtistId: null, initUserData: null }
    }
  })()

  const [permissions, setPermissions] = useState<UserPermissions | null>(initPerms)
  const [loading, setLoading] = useState(initLoading)
  // Sync primary artist id so we re-fetch when user toggles artists on/off in Team Hub
  const [primaryArtistId, setPrimaryArtistId] = useState<string | null>(() => initPrimaryArtistId || getPrimaryArtistId())
  const userDataRef = useRef(initUserData)
  userDataRef.current = initUserData
  /** Team membership document id from API — `dizee:granularPerms` is often keyed by this, not User._id */
  const teamMembershipRecordIdRef = useRef<string | null>(null)

  useEffect(() => {
    const sync = () => setPrimaryArtistId(getPrimaryArtistId())
    sync()
    window.addEventListener('dizee:activeArtistsChanged', sync)
    return () => window.removeEventListener('dizee:activeArtistsChanged', sync)
  }, [])

  /** When team members enable multiple artists (blended view), refetch even if primary id is unchanged. */
  const [activeScopeKey, setActiveScopeKey] = useState(() => getActiveArtistIds().join('|'))
  useEffect(() => {
    const sync = () => setActiveScopeKey(getActiveArtistIds().join('|'))
    sync()
    window.addEventListener('dizee:activeArtistsChanged', sync)
    return () => window.removeEventListener('dizee:activeArtistsChanged', sync)
  }, [])

  useEffect(() => {
    const primaryId = primaryArtistId
    const userData = userDataRef.current
    if (!initIsTeamMember || !primaryId || !userData) {
      if (initIsTeamMember && !primaryId) {
        setPermissions({ sections: ['team', 'settings', 'schedule'], sectionConfig: {} })
        setLoading(false)
      }
      return
    }

    setLoading(true)
    const fetchPermissions = async () => {
      try {
        const userData = userDataRef.current!
        const role = (userData.professionRole === 'support'
          ? 'support'
          : userData.professionRole === 'promoter'
            ? 'promoter'
            : 'manager') as 'manager' | 'support' | 'promoter'

        const activeIds = getActiveArtistIds()

        const mergeSectionConfigWithDefaults = (sectionConfig: SectionGranularConfig | null): SectionGranularConfig => {
          const defaults = getDefaultSectionConfig(role)
          const mergedSectionConfig: SectionGranularConfig = { ...defaults }
          if (sectionConfig && typeof sectionConfig === 'object') {
            for (const key of Object.keys(sectionConfig) as (keyof SectionGranularConfig)[]) {
              const backendVal = (sectionConfig as any)[key]
              const defaultVal = (defaults as any)[key]
              if (
                backendVal &&
                typeof backendVal === 'object' &&
                !Array.isArray(backendVal) &&
                defaultVal &&
                typeof defaultVal === 'object' &&
                !Array.isArray(defaultVal)
              ) {
                ;(mergedSectionConfig as any)[key] = { ...defaultVal, ...backendVal }
              } else {
                ;(mergedSectionConfig as any)[key] = backendVal
              }
            }
          }
          return mergedSectionConfig
        }

        const applyRoleDefaults = () => {
          console.warn('[useUserPermissions] Could not resolve permissions — granting role defaults')
          if (role === 'manager') {
            setPermissions({
              sections: ['team', 'settings', 'shows', 'schedule', 'music', 'links', 'knowledge'],
              sectionConfig: getDefaultSectionConfig('manager'),
            })
          } else if (role === 'promoter') {
            setPermissions({
              sections: ['team', 'settings', 'shows', 'schedule'],
              sectionConfig: getDefaultSectionConfig('promoter'),
            })
          } else {
            setPermissions({
              sections: ['team', 'settings', 'shows', 'schedule'],
              sectionConfig: getDefaultSectionConfig('support'),
            })
          }
        }

        if (activeIds.length <= 1) {
          const resolved = await resolvePermissionsForArtist(primaryId, userData)
          teamMembershipRecordIdRef.current = resolved.memberId
          console.log(
            '[useUserPermissions] single artist resolved — sections:',
            resolved.sections,
            'apps:',
            resolved.apps,
            'sectionConfig:',
            resolved.sectionConfig,
          )

          // Persist sectionConfig so isDealFinancialsHiddenForArtist works on any device
          if (resolved.sectionConfig) {
            try {
              const json = JSON.stringify(resolved.sectionConfig)
              const keys = [userData._id, resolved.memberId].filter(
                (k): k is string => typeof k === 'string' && k.length > 0,
              )
              for (const k of keys) {
                localStorage.setItem(`dizee:granularPerms:${primaryId}:${k}`, json)
              }
            } catch { /* quota */ }
          }

          const mergedSectionConfig = mergeSectionConfigWithDefaults(resolved.sectionConfig)

          if (resolved.sections && resolved.sections.length > 0) {
            const finalSections = [...new Set(['settings', ...resolved.sections])]
            setPermissions({
              sections: finalSections,
              sectionConfig: mergedSectionConfig,
            })
          } else if (resolved.apps && resolved.apps.length > 0) {
            setPermissions({
              sections: deriveSections(resolved.apps),
              sectionConfig: mergedSectionConfig,
            })
          } else {
            applyRoleDefaults()
          }
        } else {
          const resolvedList = await Promise.all(activeIds.map((aid) => resolvePermissionsForArtist(aid, userData)))
          const primaryResolved = resolvedList[0]
          teamMembershipRecordIdRef.current = primaryResolved.memberId

          console.log(
            '[useUserPermissions] blended scope — artists:',
            activeIds.length,
            'per-artist sections:',
            resolvedList.map((r) => r.sections),
          )

          // Persist each artist's sectionConfig so isDealFinancialsHiddenForArtist
          // can resolve financialAccess for non-primary artists on any device.
          for (let i = 0; i < resolvedList.length; i++) {
            const r = resolvedList[i]
            const aid = activeIds[i]
            if (r.sectionConfig) {
              try {
                const json = JSON.stringify(r.sectionConfig)
                const keys = [userData._id, r.memberId].filter(
                  (k): k is string => typeof k === 'string' && k.length > 0,
                )
                for (const k of keys) {
                  localStorage.setItem(`dizee:granularPerms:${aid}:${k}`, json)
                }
              } catch { /* quota */ }
            }
          }

          const mergedSectionConfig = mergeSectionConfigWithDefaults(primaryResolved.sectionConfig)

          const unionSections = new Set<string>()
          for (const r of resolvedList) {
            if (r.sections && r.sections.length > 0) {
              r.sections.forEach((s) => unionSections.add(s))
            } else if (r.apps && r.apps.length > 0) {
              deriveSections(r.apps).forEach((s) => unionSections.add(s))
            }
          }

          if (unionSections.size > 0) {
            const finalSections = [...new Set(['settings', ...unionSections])]
            setPermissions({
              sections: finalSections,
              sectionConfig: mergedSectionConfig,
            })
          } else if (primaryResolved.apps && primaryResolved.apps.length > 0) {
            setPermissions({
              sections: deriveSections(primaryResolved.apps),
              sectionConfig: mergedSectionConfig,
            })
          } else {
            applyRoleDefaults()
          }
        }

        setLoading(false)
      } catch (error) {
        console.error('[useUserPermissions] Unexpected error:', error)
        const ud = userDataRef.current
        const role = (ud?.professionRole === 'support'
          ? 'support'
          : ud?.professionRole === 'promoter'
            ? 'promoter'
            : 'manager') as 'manager' | 'support' | 'promoter'
        if (role === 'manager') {
          setPermissions({
            sections: ['team', 'settings', 'shows', 'schedule', 'music', 'links', 'knowledge'],
            sectionConfig: getDefaultSectionConfig('manager'),
          })
        } else if (role === 'promoter') {
          setPermissions({
            sections: ['team', 'settings', 'shows', 'schedule'],
            sectionConfig: getDefaultSectionConfig('promoter'),
          })
        } else {
          setPermissions({
            sections: ['team', 'settings', 'shows', 'schedule'],
            sectionConfig: getDefaultSectionConfig('support'),
          })
        }
        setLoading(false)
      }
    }

    fetchPermissions()
  }, [initIsTeamMember, primaryArtistId, activeScopeKey])

  /** Persist last resolved permissions so the next load can show the same sidebar until the API returns. */
  useEffect(() => {
    if (!initIsTeamMember || !permissions || !primaryArtistId) return
    const email = userDataRef.current?.email
    if (typeof email !== 'string' || !email) return
    writeTeamPermissionsSnapshot(email, primaryArtistId, permissions)
  }, [permissions, primaryArtistId, initIsTeamMember])

  // ─── Accessors ───────────────────────────────────────────────────

  /** Check if the user can visit a section at all */
  const hasPermission = (sectionId: string): boolean => {
    if (permissions === null) {
      if (!initIsTeamMember) return true // artist = full access
      return false // team, no snapshot yet — sidebar shows only unconditional items
    }
    if (!permissions) return false
    return permissions.sections.includes(sectionId)
  }

  /** Get the granular config for a specific section (null = unrestricted / artist) */
  const getSectionConfig = <T = any>(sectionId: string): T | null => {
    if (permissions === null) return null // artist = no restrictions
    if (!permissions?.sectionConfig) return null
    return (permissions.sectionConfig as any)[sectionId] ?? null
  }

  /** Convenience: get Shows config */
  const getShowsConfig = (): ShowsGranularConfig | null => getSectionConfig<ShowsGranularConfig>('shows')

  /** Convenience: get Music config */
  const getMusicConfig = (): MusicGranularConfig | null => getSectionConfig<MusicGranularConfig>('music')

  /** Convenience: get Schedule config */
  const getScheduleConfig = (): ScheduleGranularConfig | null => getSectionConfig<ScheduleGranularConfig>('schedule')

  /** Convenience: get Overview config */
  const getOverviewConfig = (): OverviewGranularConfig | null => getSectionConfig<OverviewGranularConfig>('overview')

  /** Convenience: get Finance config */
  const getFinanceConfig = (): FinanceGranularConfig | null => getSectionConfig<FinanceGranularConfig>('finance')

  /** Convenience: get Tasks config */
  const getTasksConfig = (): TasksGranularConfig | null => getSectionConfig<TasksGranularConfig>('tasks')

  /** Convenience: get Knowledge config */
  const getKnowledgeConfig = (): KnowledgeGranularConfig | null => getSectionConfig<KnowledgeGranularConfig>('knowledge')

  /** Check if user is a team member (not an artist) — use role, not permissions (null during first paint without snapshot). */
  const isTeamMember = initIsTeamMember

  /** Check if a section is view-only for this user */
  const isViewOnly = (sectionId: string): boolean => {
    if (permissions === null) {
      if (!initIsTeamMember) return false
      return true // team, no snapshot: conservative until API returns
    }
    const config = getSectionConfig<{ accessLevel?: AccessLevel }>(sectionId)
    if (!config) return false
    return config.accessLevel === 'view'
  }

  /**
   * Whether monetary / financial detail should be hidden for this section (team members).
   * - overview, shows, music: granular `financialAccess` on that section.
   * - finance (Money page): need Finance section + overview financial opt-in.
   * - deals: dollar amounts / pipeline need Finance section + same overview opt-in (deals page stays visible).
   */
  const isFinancialHidden = (sectionId: string): boolean => {
    if (permissions === null) {
      if (!initIsTeamMember) return false
      return true // team, no snapshot: hide $ until loaded
    }

    const overviewFinancialOff = (): boolean => {
      const ov = getSectionConfig<{ financialAccess?: boolean }>('overview')
      if (!ov) return true
      return ov.financialAccess !== true
    }

    if (sectionId === 'finance') {
      if (!hasPermission('finance')) return true
      return overviewFinancialOff()
    }

    if (sectionId === 'deals') {
      if (!hasPermission('finance')) return true
      return overviewFinancialOff()
    }

    const config = getSectionConfig<{ financialAccess?: boolean }>(sectionId)
    if (!config) return true // no config = gate financials
    return config.financialAccess !== true
  }

  /**
   * Deal pipeline / expected value / payment — per artist account.
   * Finance section + overview financial opt-in for that artist (localStorage for non-primary).
   */
  const isDealFinancialsHiddenForArtist = useCallback(
    (artistId: string | undefined): boolean => {
      if (permissions === null) {
        if (!initIsTeamMember) return false
        return true
      }
      if (!permissions.sections.includes('finance')) return true
      const aid = artistId || primaryArtistId || ''
      if (!aid) return true
      const overviewOff = (ov: { financialAccess?: boolean } | null | undefined) =>
        !ov || ov.financialAccess !== true

      const permOv = permissions.sectionConfig?.overview
      // `permissions` are loaded for `primaryArtistId` only. A grant on the primary account must not
      // expose deal/money amounts for other roster artists — those need their own overview grant (API or storage).
      if (aid === primaryArtistId && permOv?.financialAccess === true) return false

      // Same key Team Management uses — API merge can omit or default overview.financialAccess; storage is the artist-saved grant.
      try {
        const ud = userDataRef.current
        const keys = [ud?._id, teamMembershipRecordIdRef.current].filter(
          (k): k is string => typeof k === 'string' && k.length > 0,
        )
        for (const k of keys) {
          const raw = localStorage.getItem(`dizee:granularPerms:${aid}:${k}`)
          if (raw) {
            const cfg = JSON.parse(raw) as SectionGranularConfig
            if (cfg.overview?.financialAccess === true) return false
          }
        }
      } catch {
        /* ignore */
      }

      if (aid === primaryArtistId) {
        return overviewOff(permOv)
      }
      return true
    },
    [permissions, primaryArtistId, initIsTeamMember],
  )

  return {
    permissions,
    loading,
    isTeamMember,
    hasPermission,
    isViewOnly,
    isFinancialHidden,
    isDealFinancialsHiddenForArtist,
    getSectionConfig,
    getShowsConfig,
    getMusicConfig,
    getScheduleConfig,
    getOverviewConfig,
    getFinanceConfig,
    getTasksConfig,
    getKnowledgeConfig,
  }
}
